/**
 * Checklist Mapper
 * Maps between API DTOs and Domain Entities
 */

import {
  ChecklistCountsEntity,
  ChecklistProgressEntity,
  ChecklistItemEntity,
  ChecklistAttachmentEntity,
  ChecklistItemsResultEntity,
  PaginationInfoEntity,
} from "../../domain/entities/ChecklistEntity";
import {
  ChecklistCountsDTO,
  ChecklistProgressDTO,
  ChecklistItemDTO,
  ChecklistItemAttachmentDTO,
  ChecklistItemsResponseDTO,
  ChecklistCountsResponseDTO,
  ChecklistProgressResponseDTO,
  PaginationDTO,
} from "../models/ChecklistDTOs";

export class ChecklistMapper {
  /**
   * Map ChecklistItemAttachmentDTO to ChecklistAttachmentEntity
   */
  static toChecklistAttachmentEntity(
    dto: ChecklistItemAttachmentDTO
  ): ChecklistAttachmentEntity {
    return {
      id: dto.id,
      checklist_item_id: dto.checklist_item_id,
      file_name: dto.file_name,
      original_filename: dto.original_filename,
      file_size: dto.file_size,
      mime_type: dto.mime_type,
      s3_bucket: dto.s3_bucket,
      s3_key: dto.s3_key,
      s3_url: dto.s3_url,
      uploaded_by: dto.uploaded_by,
      uploaded_by_name: dto.uploaded_by_name,
      uploaded_by_email: dto.uploaded_by_email,
      uploaded_at: dto.uploaded_at,
    };
  }

  /**
   * Map ChecklistItemDTO to ChecklistItemEntity
   */
  static toChecklistItemEntity(dto: ChecklistItemDTO): ChecklistItemEntity {
    return {
      id: dto.id,
      checklist_id: dto.checklist_id,
      template_item_id: dto.template_item_id,
      status: dto.status,
      title: dto.title,
      description: dto.description,
      accounting_area: dto.accounting_area,
      module: dto.module,
      task_guidance: Array.isArray(dto.task_guidance)
        ? dto.task_guidance
        : [],
      requires_proof: dto.requires_proof,
      accepted_proof_types: Array.isArray(dto.accepted_proof_types)
        ? dto.accepted_proof_types
        : ["screenshot"],
      order_index: dto.order_index,
      priority:
        dto.priority === "high" ||
        dto.priority === "medium" ||
        dto.priority === "low"
          ? dto.priority
          : undefined,
      link_url: dto.link_url,
      link_text: dto.link_text,
      notes: dto.notes,
      system_info: dto.system_info,
      completed_by: dto.completed_by,
      completed_at: dto.completed_at,
      closed_by: dto.closed_by,
      attachments: (dto.attachments || []).map((attachment) =>
        this.toChecklistAttachmentEntity(attachment)
      ),
      review_status: dto.review_status,
      review_comments: dto.review_comments,
      reviewed_at: dto.reviewed_at,
      reviewer_id: dto.reviewer_id,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  /**
   * Map PaginationDTO to PaginationInfoEntity
   */
  static toPaginationInfoEntity(dto: PaginationDTO): PaginationInfoEntity {
    return {
      total: dto.total,
      limit: dto.limit,
      offset: dto.offset,
      hasMore: dto.has_more,
    };
  }

  /**
   * Map ChecklistItemsResponseDTO to ChecklistItemsResultEntity
   */
  static toChecklistItemsResultEntity(
    dto: ChecklistItemsResponseDTO
  ): ChecklistItemsResultEntity {
    return {
      items: dto.data.items.map((item) => this.toChecklistItemEntity(item)),
      pagination: this.toPaginationInfoEntity(dto.data.pagination),
    };
  }

  /**
   * Map ChecklistCountsResponseDTO to ChecklistCountsEntity
   */
  static toChecklistCountsEntity(
    dto: ChecklistCountsResponseDTO
  ): ChecklistCountsEntity {
    const raw = dto.data as Record<string, Record<string, number>>;
    const normalized: ChecklistCountsEntity = {};
    for (const [area, row] of Object.entries(raw)) {
      normalized[area] = {
        pending: Number(row?.pending ?? 0),
        done: Number(row?.done ?? row?.approved ?? 0),
        rejected: Number(row?.rejected ?? 0),
        to_be_done_later: Number(row?.to_be_done_later ?? 0),
        total: Number(row?.total ?? 0),
      };
    }
    return normalized;
  }

  /**
   * Map ChecklistProgressResponseDTO to ChecklistProgressEntity
   */
  static toChecklistProgressEntity(
    dto: ChecklistProgressResponseDTO
  ): ChecklistProgressEntity {
    return dto.data;
  }
}

